import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { renameFolder, deleteFolder } from '@/services/api/endpoints';
import {
  copyFolderApi,
  moveFolderApi,
  type CopyFolderArgs,
  type CopyFolderResult,
  type MoveFolderArgs,
} from '@/services/api/folders';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import type { Folder } from '@/types/domain';

/**
 * Invalide l'arborescence, et TOUJOURS les résultats de recherche.
 *
 * `['search']` n'était invalidé nulle part (hormis un changement de visibilité) :
 * tant que les actions n'existaient que dans l'arbre, la liste de résultats
 * n'était pas à l'écran au moment de la mutation. Depuis que les menus de la
 * recherche proposent renommer / déplacer / supprimer, l'oubli se voit : la ligne
 * resterait affichée sous son ANCIEN nom, ou resterait cliquable après une
 * suppression — et un clic sur un dossier supprimé ouvre un onglet vide.
 *
 * `fn_search` reclasse aussi les résultats (le score dépend du conteneur), donc
 * un déplacement inter-espaces change la liste sans changer aucun nom.
 */
function invalidateTree(qc: QueryClient, opts: { mails?: boolean } = {}): void {
  void qc.invalidateQueries({ queryKey: ['folders'] });
  if (opts.mails) void qc.invalidateQueries({ queryKey: ['mails'] });
  void qc.invalidateQueries({ queryKey: ['search'] });
}

export function useRenameFolder() {
  const qc = useQueryClient();
  return useMutation<Folder, Error, { folderId: string; name: string }>({
    mutationFn: async (args) => {
      addBreadcrumb({ category: 'mutation', message: 'rename-folder', data: args });
      return renameFolder(args);
    },
    onSuccess: () => {
      invalidateTree(qc);
    },
  });
}

export function useMoveFolder() {
  const qc = useQueryClient();
  return useMutation<Folder, Error, MoveFolderArgs>({
    mutationFn: async (args) => {
      addBreadcrumb({ category: 'mutation', message: 'move-folder', data: { ...args } });
      return moveFolderApi(args);
    },
    onSuccess: () => {
      // Un déplacement inter-espaces change les deux arborescences ; on invalide
      // aussi les mails, dont le `containerId` affiché suit le dossier.
      invalidateTree(qc, { mails: true });
    },
  });
}

/**
 * Copie PROFONDE d'un dossier (arborescence + mails).
 *
 * Peut durer plusieurs secondes : chaque mail est une copie d'objet S3. Les
 * points d'appel s'appuient sur `isPending` pour le signaler.
 */
export function useCopyFolder() {
  const qc = useQueryClient();
  return useMutation<CopyFolderResult, Error, CopyFolderArgs>({
    mutationFn: async (args) => {
      addBreadcrumb({ category: 'mutation', message: 'copy-folder', data: { ...args } });
      return copyFolderApi(args);
    },
    onSuccess: () => {
      // Les mails copiés apparaissent dans les nouveaux dossiers, et le
      // compteur par dossier de `outlook-list-folders` change.
      invalidateTree(qc, { mails: true });
    },
  });
}

export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation<void, Error, { folderId: string }>({
    mutationFn: async (args) => {
      addBreadcrumb({ category: 'mutation', message: 'delete-folder', data: args });
      return deleteFolder(args.folderId);
    },
    onSuccess: () => {
      // Les mails du dossier partent à la corbeille avec lui : leurs listes et
      // les résultats de recherche qui les citaient doivent suivre.
      invalidateTree(qc, { mails: true });
    },
  });
}
